import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useRouter } from '@/routing/navigation'
import Link from '@/routing/Link'
import Image from '@/routing/Image'
import { PageContent } from '@/components/layout/PageContent'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageLayout } from '@/components/layout/PageLayout'
import { populationsApi, filesApi } from '@/lib/api'
import { getApiBaseUrl } from '@/lib/runtime-env'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft, Upload, Loader2, X, Users, AlertCircle } from '@digital-twins/design-system/icons'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { audienceTierDisplayName } from '@/lib/billing/display-names'
import { CREDITS_PER_RESPONDENT } from '@/lib/billing/credit-rates'

function normalizeAudienceTier(
  raw: string | null | undefined,
): 'tier1' | 'tier2' | 'tier3' {
  if (raw === 'tier2' || raw === 'premium') return 'tier2'
  if (raw === 'tier3') return 'tier3'
  return 'tier1'
}

export default function EditAudiencePage() {
  const { t } = useTranslation('app')
  const { id: audienceId = '' } = useParams<{ id: string }>()
  const router = useRouter()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [currentUser, setCurrentUser] = useState<any>(null)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [audience, setAudience] = useState<any>(null)

  const [formData, setFormData] = useState({
    name: '',
    aiDescription: '',
    logoUrl: '',
    audienceType: '' as '' | 'B2B' | 'B2C',
    tier: 'tier1' as 'tier1' | 'tier2' | 'tier3',
    creditsPerRespondent: 1,
  })

  const [logoPreview, setLogoPreview] = useState<string | null>(null)

  useEffect(() => {
    const user = localStorage.getItem('user')
    if (!user) {
      router.replace('/login')
      return
    }

    try {
      const rawUser = localStorage.getItem('user')
      if (rawUser) {
        setCurrentUser(JSON.parse(rawUser))
      }
    } catch (error) {
      console.error('Failed to parse user from localStorage:', error)
    } finally {
      setAuthLoading(false)
    }
  }, [router])

  // Load audience data
  useEffect(() => {
    const fetchAudience = async () => {
      try {
        setLoading(true)
        const data = await populationsApi.getAudienceUiDetail(audienceId)
        setAudience(data)
        setFormData({
          name: data.name || '',
          aiDescription: data.aiDescription || data.contextText || data.description || '',
          logoUrl: data.logoUrl || '',
          audienceType: data.audienceType || '',
          tier: normalizeAudienceTier(data.tier),
          creditsPerRespondent: data.creditsPerRespondent ?? 1,
        })
        if (data.logoUrl) {
          setLogoPreview(data.logoUrl)
        }
      } catch (error) {
        console.error('Failed to fetch audience:', error)
        setError('Failed to load audience data')
      } finally {
        setLoading(false)
      }
    }
    fetchAudience()
  }, [audienceId])

  // Permission check
  const currentUserId = currentUser?.userId || currentUser?.id
  const isCreator = audience?.userId === currentUserId
  const isPlatformAdmin = currentUser?.isPlatformAdmin
  const canEdit = isCreator || isPlatformAdmin

  // Redirect if no permission
  useEffect(() => {
    if (!authLoading && !loading && audience && !canEdit) {
      router.replace(`/audiences/${audienceId}`)
    }
  }, [authLoading, loading, audience, canEdit, audienceId, router])

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return
    }

    // Show preview immediately
    const reader = new FileReader()
    reader.onload = (e) => {
      setLogoPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)

    // Upload to server
    setUploadingLogo(true)
    setError(null)
    try {
      const result = await filesApi.upload(file, 'image')
      const apiBase = getApiBaseUrl()
      const baseUrl = apiBase === '/api' ? 'http://localhost:4000' : apiBase
      setFormData(prev => ({ ...prev, logoUrl: `${baseUrl}/files/${result.token}` }))
    } catch (error) {
      console.error('Failed to upload logo:', error)
      setError('Failed to upload logo. Please try again.')
      setLogoPreview(formData.logoUrl || null)
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleRemoveLogo = () => {
    setLogoPreview(null)
    setFormData(prev => ({ ...prev, logoUrl: '' }))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    setSaving(true)
    setError(null)
    try {
      await populationsApi.update(audienceId, {
        name: formData.name.trim(),
        aiDescription: formData.aiDescription.trim() || undefined,
        logoUrl: formData.logoUrl || undefined,
        audienceType: formData.audienceType || undefined,
        tier: formData.tier,
        creditsPerRespondent: formData.creditsPerRespondent,
      })

      // Force refresh to see updated data
      router.refresh()
      router.push(`/audiences/${audienceId}`)
    } catch (error) {
      console.error('Failed to update audience:', error)
      setError(t('audienceEdit.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  if (authLoading || loading) {
    return (
      <PageLayout className="min-h-screen max-w-3xl pt-14 py-8 md:pt-8">
        <PageContent>
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-[400px] w-full rounded-2xl" />
        </PageContent>
      </PageLayout>
    )
  }

  if (!audience || !canEdit) {
    return (
      <PageLayout className="min-h-screen max-w-3xl pt-14 py-8 md:pt-8">
        <PageContent>
          <Text color="muted">You don&apos;t have permission to edit this audience.</Text>
          <Link href="/audiences">
            <Button variant="link" className="px-0">Back to Audiences</Button>
          </Link>
        </PageContent>
      </PageLayout>
    )
  }

  return (
    <PageLayout className="min-h-screen max-w-3xl pt-14 py-8 md:pt-8">
      <PageHeader
        leading={(
          <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground">
            <Link href={`/audiences/${audienceId}`}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Audience
            </Link>
          </Button>
        )}
        title={<Heading level={1}>{t('audienceEdit.title')}</Heading>}
        description="Update the details of your audience."
      />

      <PageContent className="gap-6">

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="rounded-xl">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('audienceEdit.details')}</CardTitle>
              <CardDescription>
                Basic information about your audience.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Upload */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-foreground">Logo</Label>
                <div className="flex items-start gap-4">
                  {/* Logo preview */}
                  <div className="relative">
                    {logoPreview ? (
                      <div className="h-20 w-20 rounded-xl bg-card border border-border overflow-hidden flex items-center justify-center">
                        <Image
                          src={logoPreview}
                          alt="Logo preview"
                          width={80}
                          height={80}
                          className="object-contain"
                        />
                        {uploadingLogo && (
                          <div className="absolute inset-0 bg-card/80 flex items-center justify-center">
                            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="h-20 w-20 rounded-xl bg-muted border border-border border-dashed flex items-center justify-center">
                        <Users className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    {logoPreview && !uploadingLogo && (
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/80 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>

                  {/* Upload button */}
                  <div className="flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploadingLogo}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="rounded-full"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2 h-4 w-4" />
                          {logoPreview ? 'Change Logo' : 'Upload Logo'}
                        </>
                      )}
                    </Button>
                    <Text size="xs" color="muted" className="mt-2">
                      PNG, JPG or SVG. Max 5MB.
                    </Text>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-foreground">{t('audienceEdit.name')} *</Label>
                <Input
                  id="name"
                  placeholder="e.g. US Gen Pop, Tech Executives, Enterprise Buyers"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-xl border-border"
                  required
                />
              </div>

              {/* Description (AI-generated, user-editable) */}
              <div className="space-y-2">
                <Label htmlFor="aiDescription" className="text-sm font-medium text-foreground">{t('audienceEdit.description')}</Label>
                <Textarea
                  id="aiDescription"
                  placeholder="A short description of this audience. Auto-generated by AI during upload — edit to refine."
                  value={formData.aiDescription}
                  onChange={(e) => setFormData({
                    ...formData,
                    aiDescription: e.target.value,
                  })}
                  className="min-h-[120px] rounded-xl border-border"
                />
                <Text size="xs" color="muted">
                  This description was generated by AI from your data. Edit it to better describe your audience.
                </Text>
              </div>
            </CardContent>
          </Card>

          {/* Classification */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('audienceEdit.classification')}</CardTitle>
              <CardDescription>
                Categorize your audience for easier management.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Audience Type */}
              <div className="space-y-3">
                <Label className="text-sm font-medium text-foreground">Audience Type</Label>
                <Select
                  value={formData.audienceType || undefined}
                  onValueChange={(value) =>
                    setFormData({ ...formData, audienceType: value as 'B2B' | 'B2C' | '' })
                  }
                >
                  <SelectTrigger className="w-full sm:w-[200px] rounded-xl border-border">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="B2B">B2B</SelectItem>
                    <SelectItem value="B2C">B2C</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Tier - only show to platform admins */}
              {isPlatformAdmin && (
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-foreground">Tier</Label>
                  <Select
                    value={formData.tier}
                    onValueChange={(value) => {
                      const newTier = value as 'tier1' | 'tier2' | 'tier3'
                      const newCredits = CREDITS_PER_RESPONDENT[newTier] ?? 1
                      setFormData({ ...formData, tier: newTier, creditsPerRespondent: newCredits })
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-[200px] rounded-xl border-border">
                      <SelectValue placeholder="Select tier..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tier1">{audienceTierDisplayName('tier1')}</SelectItem>
                      <SelectItem value="tier2">{audienceTierDisplayName('tier2')}</SelectItem>
                      <SelectItem value="tier3">{audienceTierDisplayName('tier3')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Text size="xs" color="muted">
                    Premium audiences have enhanced features and branding.
                  </Text>
                </div>
              )}

              {/* Credits per Respondent - only show to platform admins */}
              {isPlatformAdmin && (
                <div className="space-y-3">
                  <Label htmlFor="creditsPerRespondent" className="text-sm font-medium text-foreground">
                    Credits per Respondent
                  </Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="creditsPerRespondent"
                      type="number"
                      min={1}
                      value={formData.creditsPerRespondent}
                      onChange={(e) => setFormData({ ...formData, creditsPerRespondent: parseInt(e.target.value) || 1 })}
                      className="w-full sm:w-[120px] rounded-xl border-border"
                      disabled={formData.tier === 'tier1'}
                    />
                    <span className="text-sm text-muted-foreground">credits</span>
                  </div>
                  <Text size="xs" color="muted">
                    {formData.tier === 'tier1'
                      ? 'Standard tier audiences are fixed at 1 credit per respondent.'
                      : 'Set the credit cost per respondent for this premium audience.'}
                  </Text>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Spacer so the last card isn't hidden behind the mobile fixed bar */}
          <div className="h-28 sm:hidden" />

          {/* Actions — fixed to bottom on mobile, inline on desktop */}
          <div className="fixed bottom-0 left-0 right-0 z-20 bg-background border-t border-border px-5 pt-4 pb-10 sm:static sm:bottom-auto sm:left-auto sm:right-auto sm:z-auto sm:bg-transparent sm:border-none sm:px-0 sm:pt-0 sm:pb-0">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => router.back()}
                className="flex-1 rounded-full"
                disabled={saving}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
              <Button
                type="submit"
                disabled={saving || uploadingLogo || !formData.name.trim()}
                variant="cta"
                shape="pill"
                className="flex-1"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('saving', { ns: 'common' })}
                  </>
                ) : (
                  t('saveChanges', { ns: 'common' })
                )}
              </Button>
            </div>
          </div>
        </form>
      </PageContent>
    </PageLayout>
  )
}
